/**
 * Error Handling Utilities
 *
 * Provides safe error message extraction from various error types.
 */

/**
 * Safely extracts error message from API error response
 *
 * @param error - The caught error (can be any type)
 * @param fallback - Fallback message if error message cannot be extracted
 * @returns A string error message safe for rendering
 */
export function getErrorMessage(error: unknown, fallback: string): string {
  if (!error) return fallback;

  if (typeof error === 'string') return error;

  if (typeof error !== 'object') return fallback;

  const err = error as Record<string, unknown>;

  // Try axios error response
  const response = err.response as Record<string, unknown> | undefined;
  if (response) {
    const responseData = response.data as Record<string, unknown> | undefined;
    if (responseData) {
      if (typeof responseData.message === 'string') return responseData.message;
      if (Array.isArray(responseData.message)) {
        return responseData.message
          .map((item) => {
            if (typeof item === 'string') return item;
            if (item && typeof item === 'object' && 'message' in item) {
              return String((item as { message: unknown }).message);
            }
            return String(item);
          })
          .join(', ');
      }
    }
  }

  // Try error.message (standard Error objects)
  if (typeof err.message === 'string') return err.message;

  // Last resort: try to stringify
  try {
    const str = JSON.stringify(error);
    if (str && str !== '{}') return str.substring(0, 200);
  } catch {
    // Ignore stringify errors
  }

  return fallback;
}
